import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { arrayOf, shape, string, func, bool } from 'prop-types';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  LayoutWrapperFooter,
  Footer,
} from '../../components';
import { TopbarContainer } from '..';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import MessagePanel from '../../components/MessagePanel/MessagePanel';
import MessagesSection from '../../components/MessagePanel/MessagesSection';
import MessageMobilePanel from '../../components/MessagePanel/MessageMobilePanel';
import css from './MessagePage.css';
import { loadData, onSendMessage, loadMoreMessages } from './MessagePage.duck';
import { logEvent, getParamsListing } from '../../util/logsEvent';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { ensureListing } from '../../util/data';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { createSlug } from '../../util/urlHelpers';
import { withRouter } from 'react-router-dom';

import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';

class MessagePageComponent extends Component {
  handleSendMessage = (messageContent) => {
    const { onSendMessage } = this.props;
    const { threadId } = this.props.params;

    if (messageContent.trim()) {
      onSendMessage(threadId, messageContent);
    }
  };

  redirectToCheckoutPageWithInitialValues = (initialValues, listing) => {
    const routes = routeConfiguration();
    const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);
    this.props.callSetInitialValues(setInitialValues, initialValues);

    // Clear previous Stripe errors from store if there is any
    this.props.onInitializeCardPaymentData();

    // Redirect to CheckoutPage
    this.props.history.push(
      createResourceLocatorString(
        'CheckoutPage',
        routes,
        { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
        {}
      )
    );
  };

  handleSubmitBookingRequest = (listing, values) => {
    const { price, ...bookingData } = values;
    const tomorrow = new Date();
    tomorrow.setDate(new Date().getDate() + 360);
    const today = new Date();
    today.setDate(new Date().getDate() + 1);

    const initialValues = {
      listing,
      bookingData,
      bookingDates: {
        bookingStart: today,
        bookingEnd: tomorrow,
        customPrice: price ? price : 1,
        shippingPrice: 109
      },
      transaction: null,
      confirmPaymentError: null,
    };

    if (price) {
      logEvent('offer submitted',
        { ...getParamsListing(listing), ['offer in percentage']: parseInt((1 - price) * 100), ['offer amount']: parseInt((listing.attributes.price.amount / 100) * price) })
    } else {
      logEvent('purchase submitted', getParamsListing(listing))
    }

    this.redirectToCheckoutPageWithInitialValues(initialValues, listing);
  };

  render() {
    const { listing, otherUser, messages, currentUser, intl, fetchMessagesInProgress, sendMessageInProgress, onManageDisableScrolling, hasMorePages, params, salesCount } = this.props;

    const title = intl.formatMessage({ id: 'MessagePage.messagesTitle' });

    const mobileHeader = listing && otherUser && currentUser && (
      <MessageMobilePanel
        listing={listing}
        otherUser={otherUser}
        intl={intl}
        currentUser={currentUser}
        onPurchase={this.handleSubmitBookingRequest}
        onManageDisableScrolling={onManageDisableScrolling}
        salesCount={salesCount}
      />
    );

    const isLoading = !listing || !otherUser || !messages || !currentUser;

    return (
      <Page title={title} className={css.root}>
        <LayoutSingleColumn>
          <LayoutWrapperTopbar>
            <TopbarContainer currentPage="MessagePage" />
          </LayoutWrapperTopbar>
          <LayoutWrapperMain>
            {mobileHeader}
            <div className={css.content} style={{ flex: 1, overflowY: "auto" }}>
              {!isLoading && (
                <>
                  <div className={css.messagesContainer}>
                    <MessagesSection
                      messages={messages}
                      otherUser={otherUser}
                      currentUser={currentUser}
                      onSendMessage={this.handleSendMessage}
                      sendMessageInProgress={sendMessageInProgress}
                      loadMoreMessages={() => this.props.loadMoreMessages(params.threadId)}
                      listing={listing}
                    />
                  </div>
                  <div className={css.panelContainer}>
                    <MessagePanel
                      listing={listing}
                      otherUser={otherUser}
                      messages={messages}
                      intl={intl}
                      currentUser={currentUser}
                      onPurchase={this.handleSubmitBookingRequest}
                      onManageDisableScrolling={onManageDisableScrolling}
                      salesCount={salesCount}
                    />
                  </div>
                </>
              )}
            </div>
          </LayoutWrapperMain>
          <LayoutWrapperFooter className={css.footer}>
            <Footer />
          </LayoutWrapperFooter>
        </LayoutSingleColumn>
      </Page>
    );
  }
}

MessagePageComponent.propTypes = {
  listing: shape({
    id: string,
    attributes: shape({
      title: string,
      images: arrayOf(
        shape({
          attributes: shape({
            variants: shape({
              default: shape({
                url: string,
              }),
            }),
          }),
        })
      ),
    }),
  }),
  otherUser: shape({
    id: string,
    attributes: shape({
      profile: shape({
        displayName: string.isRequired,
      }),
    }),
  }),
  messages: arrayOf(
    shape({
      id: string.isRequired,
      message: string.isRequired,
      timestamp: string.isRequired,
      sender_id: string.isRequired,
    })
  ).isRequired,
  currentUser: shape({
    id: string.isRequired,
    attributes: shape({
      profile: shape({
        displayName: string.isRequired,
      }),
    }),
  }),
  intl: intlShape.isRequired,
  onSendMessage: func.isRequired,
  fetchMessagesInProgress: bool.isRequired,
  sendMessageInProgress: bool.isRequired,
  params: shape({
    threadId: string.isRequired,
  }).isRequired,
  callSetInitialValues: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  history: shape({
    push: func.isRequired,
  }).isRequired,
  onManageDisableScrolling: func.isRequired,
  loadMoreMessages: func.isRequired,
  hasMorePages: bool.isRequired,
};

const mapStateToProps = state => {
  const { listing, otherUser, messages, fetchMessagesInProgress, sendMessageInProgress, currentPage, totalPages, salesCount } = state.MessagePage;
  const { currentUser } = state.user;
  const hasMorePages = currentPage < totalPages;
  return { listing, otherUser, messages, currentUser, fetchMessagesInProgress, sendMessageInProgress, hasMorePages, salesCount };
};

const mapDispatchToProps = dispatch => {
  return {
    onSendMessage: (threadId, messageContent) => dispatch(onSendMessage(threadId, messageContent)),
    loadMoreMessages: (threadId) => dispatch(loadMoreMessages(threadId)),
    callSetInitialValues: (setInitialValues, values) => dispatch(setInitialValues(values)),
    onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
    onManageDisableScrolling: (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
  };
};

const MessagePage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(MessagePageComponent);

MessagePage.loadData = loadData;

export default MessagePage;
